import { computed, ref, watch } from 'vue';
import type { ConditionValueProps } from './ConditionValue.vue';
import type { ViewAndEditAttributes } from '../common/ViewAndEdit.vue';
import RailsUtility from '@/utilities/rails';
import { toast } from 'vue-sonner';
import { useInjectedConditionEditor } from './useWorkflowConditionEditorProvider';

export const useConditionValue = (props: ConditionValueProps) => {
  const conditionEditor = useInjectedConditionEditor();
  const localValuePretty = computed(() => RailsUtility.convertFromEnumFormat(props.expressionCondition.value ?? ''));
  const selectedValueString = ref<string>(localValuePretty.value);

  const prettifyOptions = () => {
    if (!props.operatorDefinition.value) return [];
    return props.operatorDefinition.value.options.map(op => RailsUtility.convertFromEnumFormat(op));
  };

  const attributes = computed(
    (): ViewAndEditAttributes => ({
      type: 'select',
      options: prettifyOptions(),
    })
  );

  const onValueChange = (val: string) => {
    const options = props.operatorDefinition.value?.options;
    const newVal = options?.find(option => option === val || option === RailsUtility.convertToEnumFormat(val))!;
    if (!newVal) {
      const description = 'An issue has occurred, please refresh and retry';
      toast.error('Failed To Save', { description });
      throw new Error(`Attempted to change workflow condition to ${val} to an unavailable option: ${options}`);
    }

    conditionEditor.changeCondition({
      index: props.index,
      value: { newVal: newVal },
    });
  };

  const onUpstreamOptionChange = () => {
    const valueOptionsForNewOperator = props.operatorDefinition.value?.options ?? [];

    if (valueOptionsForNewOperator.length === 0) {
      selectedValueString.value = '';
      conditionEditor.changeCondition({ index: props.index, value: { newVal: undefined } });
      return;
    }

    if (valueOptionsForNewOperator.includes(RailsUtility.convertToEnumFormat(localValuePretty.value))) {
      selectedValueString.value = localValuePretty.value;
    } else {
      const firstValueOption = valueOptionsForNewOperator[0];
      selectedValueString.value = RailsUtility.convertFromEnumFormat(firstValueOption);
      conditionEditor.changeCondition({
        index: props.index,
        value: { newVal: firstValueOption },
      });
    }
  };

  watch([() => props.selectedOperator, () => props.selectedProperty], () => {
    onUpstreamOptionChange();
  });

  watch(
    () => localValuePretty.value,
    value => (selectedValueString.value = value)
  );

  return { selectedValueString, attributes, onValueChange, onUpstreamOptionChange };
};
